import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import type { JobEquipmentInstallationUnitDraft } from './support/job-equipment-installations';
import { resizeInstallationUnits } from './support/job-equipment-installations';

export function JobEquipmentInstallationFields({
  lineId,
  lineName,
  quantity,
  units,
  onChange,
}: {
  lineId: string;
  lineName: string;
  quantity: number;
  units?: JobEquipmentInstallationUnitDraft[];
  onChange: (units: JobEquipmentInstallationUnitDraft[]) => void;
}) {
  if (!Number.isInteger(quantity) || quantity < 1) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
        Enter a positive whole-number quantity to confirm each physical unit.
      </div>
    );
  }

  const rows = resizeInstallationUnits(units, quantity);
  const patchUnit = (unitIndex: number, patch: Partial<JobEquipmentInstallationUnitDraft>) => {
    onChange(
      rows.map((unit) =>
        unit.unitIndex === unitIndex ? { ...unit, ...patch, dirty: true } : unit,
      ),
    );
  };

  return (
    <fieldset className="space-y-3 rounded-md border border-dashed bg-background/70 p-3">
      <legend className="px-1 text-xs font-semibold text-foreground">Installed units</legend>
      <p className="text-xs text-muted-foreground">
        Record the manufacturer serial number and confirm the installation date for every physical
        unit. You can leave all serial numbers blank when scheduling and confirm them before the job
        is completed.
      </p>
      <div className="space-y-3">
        {rows.map((unit) => {
          const prefix = `${lineName || 'Equipment'} unit ${unit.unitIndex}`;
          const fieldId = `${lineId}-${unit.unitIndex}`;
          return (
            <div
              key={unit.unitIndex}
              className="grid gap-3 rounded-md border bg-muted/20 p-3 sm:grid-cols-[auto_minmax(0,1fr)_minmax(9rem,0.65fr)] sm:items-end"
            >
              <span className="pb-2 text-xs font-semibold text-muted-foreground">
                Unit {unit.unitIndex}
              </span>
              <div className="space-y-1.5">
                <Label htmlFor={`equipment-serial-${fieldId}`} className="text-xs">
                  Serial number
                </Label>
                <Input
                  id={`equipment-serial-${fieldId}`}
                  maxLength={255}
                  value={unit.serialNumber}
                  aria-label={`${prefix} serial number`}
                  onChange={(event) =>
                    patchUnit(unit.unitIndex, { serialNumber: event.target.value })
                  }
                  className="h-9 bg-background text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`equipment-install-date-${fieldId}`} className="text-xs">
                  Installed on
                </Label>
                <Input
                  id={`equipment-install-date-${fieldId}`}
                  type="date"
                  value={unit.installDate}
                  aria-label={`${prefix} installation date`}
                  onChange={(event) =>
                    patchUnit(unit.unitIndex, { installDate: event.target.value })
                  }
                  className="h-9 bg-background text-xs"
                />
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
